import React, { createContext, useContext, useEffect, useState } from "react";
import {
  ProjectSelectionState,
  SelectedItem,
} from "features/architecture/projects/model/types/ProjectSelection";

const STORAGE_KEY = "timer:project:v3";

const EMPTY: ProjectSelectionState = {
  project: null,
  phase: [],
  block: [],
  stage: [],
  category: undefined,
};

type ProjectSelectionContextValue = {
  projectState: ProjectSelectionState;
  setProjectState: React.Dispatch<React.SetStateAction<ProjectSelectionState>>;
  isComplete: boolean;
  clearSelection: () => void;
};

const ProjectSelectionContext =
  createContext<ProjectSelectionContextValue | null>(null);

function normalizeSelectedItems(value: unknown): SelectedItem[] {
  if (!Array.isArray(value)) return [];

  return value.filter(
    (v): v is SelectedItem =>
      typeof v === "object" && v !== null && "id" in v && "label" in v,
  );
}

function normalizeProject(value: unknown): SelectedItem | null {
  if (
    typeof value === "object" &&
    value !== null &&
    "id" in value &&
    "label" in value
  ) {
    return value as SelectedItem;
  }
  return null;
}

export const ProjectSelectionProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [projectState, setProjectState] = useState<ProjectSelectionState>(
    () => {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return EMPTY;

        const parsed = JSON.parse(raw);

        return {
          project: normalizeProject(parsed.project),
          phase: normalizeSelectedItems(parsed.phase),
          block: normalizeSelectedItems(parsed.block),
          stage: normalizeSelectedItems(parsed.stage),
          category:
            typeof parsed.category === "number" ? parsed.category : undefined,
        };
      } catch {
        return EMPTY;
      }
    },
  );

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(projectState));
  }, [projectState]);

  const clearSelection = () => {
    setProjectState(EMPTY);
    localStorage.removeItem(STORAGE_KEY);
  };

  const isComplete =
    !!projectState.project &&
    projectState.phase.length > 0 &&
    projectState.block.length > 0 &&
    projectState.stage.length > 0 &&
    !!projectState.category;

  return (
    <ProjectSelectionContext.Provider
      value={{
        projectState,
        setProjectState,
        isComplete,
        clearSelection,
      }}
    >
      {children}
    </ProjectSelectionContext.Provider>
  );
};

export const useProjectSelection = () => {
  const ctx = useContext(ProjectSelectionContext);
  if (!ctx) {
    throw new Error(
      "useProjectSelection must be used within ProjectSelectionProvider",
    );
  }
  return ctx;
};
